import {
  Button,
  DataTable,
  EmptyState,
  Text,
  type DataTableColumn,
  type SortDirection,
  type TableSort,
} from '@ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../lib/format';
import type { SaleListItem } from '../api';
import { formatEuro } from '../format';
import { useReferrerName } from './Referrer';
import { useSaleRowActions } from './SaleRowActions';
import { SaleStateBadge } from './SaleBadges';

export interface SalesTableProps {
  rows: SaleListItem[];
  loading?: boolean;
  caption: string;
  /** Hide the customer column (e.g. on the customer page). */
  hideCustomer?: boolean;
  /** Hide the date column (e.g. on a market day, where all sales share it). */
  hideDate?: boolean;
  /** Hide "Vermittelt von" (e.g. on the member page). */
  hideReferrer?: boolean;
  empty?: ReactNode;
  /** Adds a summary row with count and total of the rows (e.g. when filtered). */
  showTotal?: boolean;
  /** Column sort kept by the page (e.g. in the URL); otherwise the table keeps its own. */
  sort?: TableSort | null;
  onSortChange?: (key: string, direction: SortDirection) => void;
  /** The rows come sorted by `sort` from the server (paged lists, #188). */
  presorted?: boolean;
  /**
   * Adds the action "Rechnung erstellen" / "Rechnung ansehen" to each sale
   * (market day, MKT-017).
   */
  onInvoice?: (sale: SaleListItem) => void;
}

/**
 * Columns of the sales table; also the options of the phones' sort menu
 * (§6.4). Keys are the API's sort keys (#188).
 */
export function useSaleColumns({
  hideCustomer = false,
  hideDate = false,
  hideReferrer = false,
  onInvoice,
}: {
  hideCustomer?: boolean;
  hideDate?: boolean;
  hideReferrer?: boolean;
  onInvoice?: ((sale: SaleListItem) => void) | undefined;
} = {}): DataTableColumn<SaleListItem>[] {
  const { t } = useTranslation();
  const referrerName = useReferrerName();
  // Status shows only for the few sales that are not delivered and paid, so
  // it sits as a badge behind the row's title instead of a mostly empty
  // column (#177).
  const state = (s: SaleListItem) =>
    s.status === 'delivered' && s.payment.paid ? null : (
      <SaleStateBadge status={s.status} paid={s.payment.paid} />
    );
  const invoiceLabel = (s: SaleListItem) =>
    s.invoice ? (s.invoice.number ?? t('sales.invoice.badge.draft')) : null;
  const customerName = (s: SaleListItem) =>
    s.customer?.name ?? s.market_day?.name ?? t('sales.list.anonymous');
  return [
    ...(hideCustomer
      ? []
      : [
          {
            key: 'customer',
            header: t('sales.columns.customer'),
            primary: true,
            cell: customerName,
            badge: state,
            sortValue: customerName,
          },
        ]),
    ...(hideDate
      ? []
      : [
          {
            key: 'date',
            header: t('sales.columns.date'),
            primary: hideCustomer,
            cell: (s: SaleListItem) => formatDate(s.sold_on),
            ...(hideCustomer ? { badge: state } : {}),
            sortValue: (s: SaleListItem) => s.sold_on,
          },
        ]),
    {
      key: 'items',
      header: t('sales.columns.items'),
      primary: hideCustomer && hideDate,
      truncate: true,
      cell: (s) => s.summary,
      ...(hideCustomer && hideDate ? { badge: state } : {}),
    },
    ...(hideReferrer
      ? []
      : [
          {
            key: 'referrer',
            header: t('sales.form.referredBy'),
            mobile: 'hidden' as const,
            cell: (s: SaleListItem) => referrerName(s.referred_by),
            sortValue: (s: SaleListItem) => referrerName(s.referred_by),
          },
        ]),
    {
      key: 'total',
      header: t('sales.columns.total'),
      align: 'end',
      cell: (s) => (
        <Text as="span" numeric>
          {formatEuro(s.total_cents)}
        </Text>
      ),
      sortValue: (s) => s.total_cents,
    },
    {
      key: 'invoice',
      header: t('sales.columns.invoice'),
      mobile: onInvoice ? 'subtitle' : 'hidden',
      // On phones only the number: the action sits in the row menu "…" (§ 6).
      mobileCell: invoiceLabel,
      cell: (s) => {
        const label = invoiceLabel(s);
        if (!onInvoice || s.status === 'draft' || s.status === 'cancelled') return label;
        return (
          <Button variant="ghost" size="sm" onClick={() => onInvoice(s)}>
            {s.invoice ? t('sales.quick.view', { number: label }) : t('sales.quick.create')}
          </Button>
        );
      },
      sortValue: (s) => s.invoice?.number,
    },
  ];
}

/** Sales with status, payment and invoice; rows open the sale (SAL-007). */
export function SalesTable({
  rows,
  loading = false,
  caption,
  hideCustomer = false,
  hideDate = false,
  hideReferrer = false,
  empty,
  showTotal = false,
  sort,
  onSortChange,
  presorted = false,
  onInvoice,
}: SalesTableProps) {
  const { t } = useTranslation();
  const { rowActions, dialogs } = useSaleRowActions(onInvoice);
  const columns = useSaleColumns({ hideCustomer, hideDate, hideReferrer, onInvoice });
  const footer = showTotal
    ? {
        [columns[0]?.key ?? 'items']: t('sales.list.footer', { count: rows.length }),
        total: formatEuro(rows.reduce((sum, s) => sum + s.total_cents, 0)),
      }
    : undefined;
  return (
    <>
      <DataTable
        caption={caption}
        rows={rows}
        rowKey={(s) => s.id}
        rowHref={(s) => `/verkauf/${s.id}`}
        loading={loading}
        columns={columns}
        footer={footer}
        {...(sort !== undefined ? { sort } : {})}
        {...(onSortChange ? { onSortChange } : {})}
        presorted={presorted}
        rowActions={rowActions}
        empty={empty ?? <EmptyState title={t('sales.list.emptyTitle')} />}
      />
      {dialogs}
    </>
  );
}
